import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NeoButton from "../../../components/Common/NeoButton";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import NeoSearch from "../../../components/Common/NeoSearch";
import NeoDropdown from "../../../components/Common/NeoDropdown";
import NeoTable from "../../../components/Common/NeoTable";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import usePlatformMetrics from "./hooks/usePlatformMetrics";
import { CHURN_RISK_LABELS, CHURN_RISK_COLORS, RISK_FILTER_OPTIONS } from "./constants";

const healthColor = (score) => {
    if (score === null || score === undefined) return "muted";
    if (score >= 70) return "success";
    if (score >= 50) return "warning";
    return "danger";
};

const PlatformMetrics = () => {
    const { items, loading } = usePlatformMetrics();
    const navigate = useNavigate();

    const [filterRisk, setFilterRisk] = useState("all");
    const [search, setSearch] = useState("");

    const filteredMetrics = items.filter(m => {
        const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
        const matchesRisk = filterRisk === "all" || m.churnRisk === filterRisk;
        return matchesSearch && matchesRisk;
    });

    const openTenant = (id) => navigate(`/platform/tenants/${id}`);

    const columns = [
        {
            key: "name",
            label: "Academia",
            width: "30%",
            render: (row) => (
                <span
                    className="fw-bold text-dark fs-6"
                    style={{ cursor: "pointer" }}
                    onClick={() => openTenant(row.id)}
                    title="Ver detalhes do cliente"
                >
                    {row.name}
                </span>
            ),
        },
        {
            key: "lastAccess",
            label: "Última Atividade",
            width: "16%",
            render: (row) => (
                <span className={`fw-semibold ${row.inactiveDays !== null && row.inactiveDays <= 1 ? "text-success" : row.inactiveDays !== null && row.inactiveDays > 14 ? "text-danger" : "text-secondary"}`}>
                    {row.lastAccess}
                </span>
            ),
        },
        {
            key: "healthScore",
            label: "Health Score",
            width: "13%",
            render: (row) => (
                <NeoBadge color={healthColor(row.healthScore)}>
                    {row.healthScore !== null && row.healthScore !== undefined ? `${row.healthScore} pts` : "Não calculado"}
                </NeoBadge>
            ),
        },
        {
            key: "planId",
            label: "Plano",
            width: "10%",
            render: (row) => <span className="fw-semibold text-secondary text-uppercase">{row.planId || "—"}</span>,
        },
        {
            key: "churnRisk",
            label: "Risco",
            width: "13%",
            render: (row) => (
                <NeoBadge color={CHURN_RISK_COLORS[row.churnRisk] || "muted"}>
                    {CHURN_RISK_LABELS[row.churnRisk] || row.churnRisk}
                </NeoBadge>
            ),
        },
        {
            key: "actions",
            label: "Ação Recomendada",
            width: "18%",
            align: "center",
            render: (row) => (
                <div className="d-flex justify-content-center">
                    {row.churnRisk === "baixo" && <span className="text-xs text-muted">Nenhuma</span>}
                    {row.churnRisk === "medio" && <NeoButton variant="soft-secondary" size="sm" onClick={() => openTenant(row.id)}>Acompanhar</NeoButton>}
                    {row.churnRisk === "alto" && <NeoButton variant="warning" size="sm" onClick={() => openTenant(row.id)}>Agendar Contato</NeoButton>}
                    {row.churnRisk === "critico" && <NeoButton variant="danger" size="sm" onClick={() => openTenant(row.id)}>Ver Cliente</NeoButton>}
                </div>
            ),
        },
    ];

    const currentRiskLabel = RISK_FILTER_OPTIONS.find(o => o.value === filterRisk)?.label || "Risco Churn";

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Métricas" />
            </div>

            <NeoCard
                title="Métricas de Uso e Engajamento"
                subtitle="Monitore a atividade das academias e identifique preventivamente contas em risco de cancelamento (Churn)."
                headerIcon="mdi-chart-line"
                thinBorder={true}
                bodyClassName="p-0"
            >
                <NeoTable
                    columns={columns}
                    data={filteredMetrics}
                    loading={loading}
                    emptyMessage="Nenhuma métrica de engajamento encontrada"
                    searchComponent={
                        <div className="d-flex align-items-center gap-3">
                            <NeoSearch
                                size="sm"
                                placeholder="Buscar academia..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClear={() => setSearch("")}
                                style={{ width: "240px" }}
                            />
                            <div className="d-flex align-items-center gap-2">
                                <span className="neo-table-topbar-label m-0">Risco Churn:</span>
                                <NeoDropdown
                                    label={currentRiskLabel}
                                    variant="soft-secondary"
                                    size="sm"
                                    items={RISK_FILTER_OPTIONS.map(o => ({
                                        label: o.label,
                                        onClick: () => setFilterRisk(o.value),
                                    }))}
                                />
                            </div>
                        </div>
                    }
                />
            </NeoCard>
        </div>
    );
};

export default PlatformMetrics;
